import { z } from 'zod'
import type { IntlShape } from 'react-intl'
import { getRequiredFields } from 'util/getZodSchemaRequiredFields'
import { ReadingStatus } from 'ui/ManagerFinance/enum'

export const meterReadingFormSchema = z.object({
	companyUUID: z.string().trim(),
	meterUUID: z.string().uuid().trim(),
	status: z.nativeEnum(ReadingStatus).nullable(),
	date: z.string().date(),
	value: z.number().nullable(),
})

export const requiredFormFields = getRequiredFields(meterReadingFormSchema)

export const meterRegistryFormSchemaLocalized = (
	intl: IntlShape,
	registryMeters: Array<{
		uuid: string
		initialReading?: any
		finalReading?: any
	}>,
) => {
	const baseSchema = meterReadingFormSchema

	const localizedSchema = baseSchema.extend({
		meterUUID: baseSchema.shape.meterUUID.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Mittari vaaditaan.',
				description: 'Error message for required input: Meter',
			}),
		}),
		status: baseSchema.shape.status.refine(
			(val) => val !== undefined && val !== null,
			{
				message: intl.formatMessage({
					defaultMessage: 'Status vaaditaan.',
					description: 'Error message for required input: Status',
				}),
			},
		),
		date: baseSchema.shape.date.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Päivämäärä vaaditaan.',
				description: 'Error message for required input: Date',
			}),
		}),
		value: baseSchema.shape.value.refine((val) => val !== null && val >= 1, {
			message: intl.formatMessage({
				defaultMessage: 'Arvo vaaditaan.',
				description: 'Error message for required input: Value',
			}),
		}),
	})

	return localizedSchema.superRefine((data, ctx) => {
		const { meterUUID, status } = data
		const meter = registryMeters.find((r) => r.uuid === meterUUID)

		if (!meter) {
			return
		}

		if (
			status === ReadingStatus.INITIAL_READING &&
			meter.initialReading != null
		) {
			ctx.addIssue({
				path: ['status'],
				code: z.ZodIssueCode.custom,
				message: intl.formatMessage({
					defaultMessage: 'Mittarilla on jo ilmoitettu alkulukema.',
					description:
						'Error message for already existing initial reading for the meter.',
				}),
			})
		}

		if (status === ReadingStatus.FINAL_READING && meter.finalReading != null) {
			ctx.addIssue({
				path: ['status'],
				code: z.ZodIssueCode.custom,
				message: intl.formatMessage({
					defaultMessage: 'Mittarilla on jo ilmoitettu loppulukema.',
					description:
						'Error message for already existing final reading for the meter.',
				}),
			})
		}
	})
}
